import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFrameLoad } from "./AppFrameLoad";
import { AppAvatarLoader } from "./AppAvatars";
import { AppMediaRules } from "./AppMediaRules";
import { AppSource } from "./AppSource";

// AppMediaFill - puts the photos and voice notes into a contact page that is shown in a `blob:`
// frame, where they cannot load themselves. `AppMediaRules` has the reason; this is the act.
//
// ⚠ ONE FILE AT A TIME. A page carries at most the twenty newest messages, so the total is small,
// but twenty parallel downloads on a phone connection would make the first photo appear LAST.
// Sequential, in page order, means the one nearest the top of what is read arrives first.
//
// ⛔ EVERY BLOB URL IS REVOKED WHEN THE FRAME IS REPLACED (`Release`). Each one pins its whole file in
// memory until it is, and a session that opens fifty contacts would otherwise hold every photo it
// ever showed. A download still in flight when the frame goes is revoked the moment it lands.
//
// ⛔ A FAILED FILE IS FATE 1: the element keeps its "Photo" alt text (and is marked
// `data-wac-failed` for a test or a stylesheet), the next file is tried, and nothing is reported -
// a photo that has not synced yet, or a ticket that expired, is a normal state, and flipping the
// gate would blank the conversation the subscriber is reading.

/** The two things this needs from an element - an `Element` satisfies it by shape. */
export interface IMediaElement {
    getAttribute: (Name: string) => string | null;
    setAttribute: (Name: string, Value: string) => void;
}

/** The one thing this needs from the frame's document - a `Document` satisfies it by shape. */
export interface IMediaDoc {
    querySelectorAll: (Selector: string) => ArrayLike<IMediaElement>;
    addEventListener?: (Type: string, Listener: (Evt: unknown) => void, Capture?: boolean) => void;
}

/** The frame, as far as this is concerned. An `HTMLIFrameElement` satisfies it by shape. */
export interface IMediaFrame {
    contentDocument: IMediaDoc | null;
    addEventListener: (Type: string, Listener: () => void) => void;
}

export class AppMediaFill {
    private Source: AppSource;
    private PageHref: string;
    private Frame: IMediaFrame | null = null;
    private Queue: IMediaElement[] = [];
    private At: IMediaElement | null = null;
    private Made: string[] = [];
    private Filled: boolean = false;
    private Stopped: boolean = false;
    private ChatId: string = "";
    private Downloads: AppMediaDownloadClick[] = [];
    private Tried: boolean = false;

    public constructor(Source: AppSource, PageHref: string, ChatId: string = "") {
        this.Source = Source;
        this.PageHref = nz(PageHref);
        this.ChatId = nz(ChatId);
    }

    /** Fill the frame once its document has loaded. ⚠ A fragment jump does not fire `load` again. */
    public Attach = (Frame: IMediaFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.Attach");
        }
    };

    public OnLoad = (): void => {
        AppFrameLoad.Run(this.Frame, this.Fill, "AppMediaFill.OnLoad");
    };

    /** Queue every `data-wac-media` element and start. ⚠ Once per page: a reload must not double. */
    public Fill = (Doc: IMediaDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Filled === true || this.Stopped === true) { return; }
            this.Filled = true;
            const Buttons = Doc.querySelectorAll("[data-wac-download]");
            for (let I = 0; I < Buttons.length; I++) {
                const Button = Buttons[I] as unknown as HTMLElement;
                if (typeof Button.addEventListener !== "function") { continue; }
                const Download = new AppMediaDownloadClick(this.Source, this.ChatId, Button);
                this.Downloads.push(Download); Download.Attach();
            }
            // ⛔ (2026-10-22) A LIVE BUBBLE'S BUTTON (`AppBubbleKinds.DownloadNode`) arrives after this scan - one delegated
            // listener gives it the same click as the page's own buttons.
            if (typeof Doc.addEventListener === "function") { Doc.addEventListener("click", this.OnDocClick, true); }
            const Found: ArrayLike<IMediaElement> = Doc.querySelectorAll("[" + AppMediaRules.Attr + "]");
            for (let At: number = 0; At < Found.length; At = At + 1) { this.Queue.push(Found[At]); }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.Fill");
        }
    };

    /** A click on a `data-wac-download` button no `AppMediaDownloadClick` holds yet (a live bubble's): attach one and press it. */
    public OnDocClick = (Evt: unknown): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Target = (Evt as { target?: { closest?: (S: string) => unknown } }).target;
            if (Target === undefined || Target === null || typeof Target.closest !== "function") { return; }
            const Button = Target.closest("[data-wac-download]") as HTMLElement | null;
            if (Button === null || typeof Button.addEventListener !== "function") { return; }
            for (const Held of this.Downloads) { if (Held.Holds(Button) === true) { return; } }
            const Download = new AppMediaDownloadClick(this.Source, this.ChatId, Button);
            this.Downloads.push(Download); Download.Attach(); Download.Press();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.OnDocClick");
        }
    };

    /**
     * ⭐ A LIVE MEDIA BUBBLE'S download button (`AppChatAppend.PlaceLive`): attached and PRESSED at once, so the photo
     * or voice note appears by itself - the same download the button would start. A button already held is left alone.
     */
    public FetchLive = (Button: unknown): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || Button === null || typeof (Button as HTMLElement).addEventListener !== "function") { return; }
            for (const Held of this.Downloads) { if (Held.Holds(Button) === true) { return; } }
            const Download = new AppMediaDownloadClick(this.Source, this.ChatId, Button as HTMLElement);
            this.Downloads.push(Download); Download.Attach(); Download.Press();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.FetchLive");
        }
    };

    /** How many blob URLs this page holds - for the suite, and for nothing else. */
    public HeldCount = (): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return this.Made.length;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.HeldCount");
            return 0;
        }
    };

    /**
     * The next element with an ALLOWED value. ⛔ A value the pattern refuses is skipped without a
     * request - it is marked failed, and never turned into a Dropbox read.
     */
    private Next = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            while (this.Stopped === false && this.Queue.length > 0) {
                const One: IMediaElement = this.Queue.shift() as IMediaElement;
                const Href: string = AppMediaRules.Resolve(this.PageHref, nz(One.getAttribute(AppMediaRules.Attr)));
                if (Href.length === 0) { One.setAttribute("data-wac-failed", "1"); continue; }
                // ⭐ THE HEADER PICTURE THE LIST (OR THE AI PANEL) ALREADY FETCHED: its blob URL, no second
                // download. ⛔ Not added to `Made` - the avatar loader owns it and keeps it for the visit.
                const Held: string | undefined = AppAvatarLoader.Shown[Href];
                if (Held !== undefined) { One.setAttribute("src", Held); continue; }
                this.At = One;
                this.Tried = false;
                this.Source.LoadMedia(Href, this.OnUrl, this.OnFail);
                return;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.Next");
        }
    };

    /** ⛔ Arriving after `Release`: revoked at once, never attached to a page that has gone. */
    public OnUrl = (Url: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a blob URL arriving must be kept track
        // of or revoked whatever else has happened, or its file stays pinned in memory.
        try {
            if (this.Stopped === true) { AppMediaFill.Revoke(Url); return; }
            this.Made.push(Url);
            const One: IMediaElement | null = this.At;
            this.At = null;
            // ⭐ A DOCUMENT (2026-10-14) gets the blob in `href` - a download (`download` attribute set by the page),
            // never a `src` that would render it.
            if (One !== null && One.getAttribute(AppMediaRules.DocAttr) !== null) { One.setAttribute("href", Url); }
            else if (One !== null) { One.setAttribute("src", Url); }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.OnUrl");
        }
    };

    /** See the header: fate 1, the element keeps its alt text, the next file is tried. */
    public OnFail = (Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a photo that cannot be read is an expected condition (not
        // synced yet, ticket expired); the element says "Photo" and the rest of the page loads.
        try {
            const One: IMediaElement | null = this.At;
            if (One !== null && this.Tried === false && this.ChatId.length > 0 && this.Stopped === false) {
                const Rel = nz(One.getAttribute(AppMediaRules.Attr));
                if (AppMediaRules.Allowed(Rel) && Rel.indexOf("Media/") === 0) {
                    this.Tried = true;
                    const Name = Rel.substring(6);
                    this.Source.DownloadMedia(this.ChatId, Name.substring(0, Name.lastIndexOf(".")), this.OnUrl, this.OnFail);
                    return;
                }
            }
            this.At = null;
            if (One !== null) { One.setAttribute("data-wac-failed", "1"); }
            this.Next();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.OnFail");
        }
    };

    /** The frame is being replaced: stop, and give every file back. */
    public Release = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - releasing memory must still happen when
        // the gate is set, or every photo this session showed stays pinned.
        try {
            this.Stopped = true;
            for (const Download of this.Downloads) { Download.Release(); }
            this.Downloads = [];
            this.Queue = [];
            for (const Url of this.Made) { AppMediaFill.Revoke(Url); }
            this.Made = [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaFill.Release");
        }
    };

    /**
     * ⚠ ONLY A `blob:` URL IS REVOKED - the local source hands back plain file URLs, which own no
     * memory and which `revokeObjectURL` would ignore anyway.
     *
     * DOCUMENTED EXCEPTION: one browser call on a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: revoking cannot fail in a way anybody could act on; the callers
     * are guarded.
     */
    private static Revoke = (Url: string): void => {
        if (nz(Url).indexOf("blob:") !== 0) { return; }
        URL.revokeObjectURL(Url);
    };
}

/** A missing attachment's explicit download button. A ticking status acknowledges long CDN/re-upload
 * waits; all object URLs and timers are released when its contact frame goes away. */
class AppMediaDownloadClick {
    private Source: AppSource; private Chat: string; private Button: HTMLElement;
    private Timer: ReturnType<typeof setInterval> | null = null;
    private Deadline: ReturnType<typeof setTimeout> | null = null;
    private Url: string = ""; private Stopped: boolean = false; private Seconds: number = 0;
    private Generation: number = 0;
    public constructor(Source: AppSource, Chat: string, Button: HTMLElement) { this.Source = Source; this.Chat = Chat; this.Button = Button; }
    public Attach = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Button.addEventListener("click", this.OnClick); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMediaDownloadClick.Attach"); }
    };
    public Holds = (Button: unknown): boolean => this.Button === Button;
    public Press = (): void => { this.OnClick(); };
    private OnClick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Timer !== null || this.Stopped) { return; }
            const Name = nz(this.Button.getAttribute("data-wac-download"));
            if (AppMediaRules.Allowed("Media/" + Name) === false) { return; }
            this.Seconds = 0; this.Button.textContent = "Downloading media… 0 s";
            this.Generation++;
            this.Button.setAttribute("aria-busy", "true"); this.Button.setAttribute("disabled", "disabled");
            this.Timer = setInterval(this.OnTick, 1000);
            this.Deadline = setTimeout(this.OnDeadline, 185000);
            const Attempt = new AppMediaClickAttempt(this, this.Generation);
            this.Source.DownloadMedia(this.Chat, Name.substring(0, Name.lastIndexOf(".")), Attempt.OnUrl, Attempt.OnFailed);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMediaDownloadClick.OnClick"); this.OnFailed("The download could not start."); }
    };
    private OnTick = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Seconds++; this.Button.textContent = "Downloading media… " + this.Seconds + " s"; }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMediaDownloadClick.OnTick"); }
    };
    public OnUrl = (Url: string, Generation: number): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: release late object URLs even after errors or navigation.
        try {
            if (this.Stopped || Generation !== this.Generation) { URL.revokeObjectURL(Url); return; }
            this.StopTimer(); this.Url = Url;
            const Name = nz(this.Button.getAttribute("data-wac-download"));
            const Doc = this.Button.ownerDocument;
            let Media: HTMLElement;
            const Type: string = AppMediaRules.TypeFor(Name);
            if (Type === "application/octet-stream") {
                // ⭐ A DOCUMENT (2026-10-14): a download link under the page's sanitised name - never an img.
                Media = Doc.createElement("a"); Media.setAttribute("class", "wkdoc");
                const Ext: string = Name.substring(Name.lastIndexOf(".") + 1);
                const Save: string = AppMediaRules.SafeDownloadName(nz(this.Button.getAttribute("data-wac-docname")), Ext);
                Media.setAttribute("download", Save); Media.textContent = Save; Media.setAttribute("href", Url);
            } else {
                if (Type.indexOf("audio/") === 0) { Media = Doc.createElement("audio"); Media.setAttribute("controls", "controls"); }
                else if (this.Button.getAttribute("data-wac-sticker") !== null) { Media = Doc.createElement("img"); Media.setAttribute("alt", "Sticker"); Media.setAttribute("class", "sticker"); }
                else { Media = Doc.createElement("img"); Media.setAttribute("alt", "Downloaded photo"); Media.setAttribute("class", "pic"); }
                Media.setAttribute("src", Url);
            }
            this.Button.insertAdjacentElement("afterend", Media);
            const Placeholder = this.Button.previousElementSibling;
            if (Placeholder !== null && Placeholder.classList.contains("nomedia")) { Placeholder.textContent = ""; }
            this.Button.textContent = "Media downloaded"; this.Button.setAttribute("aria-busy", "false");
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMediaDownloadClick.OnUrl"); }
    };
    public OnFailed = (Why: string, Generation: number = this.Generation): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: missing/expired media is expected; a visible sentence permits retry.
        if (Generation !== this.Generation) { return; }
        this.StopTimer(); if (this.Stopped) { return; }
        this.Button.textContent = Why + " Retry download"; this.Button.removeAttribute("disabled"); this.Button.setAttribute("aria-busy", "false");
    };
    private StopTimer = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: cleanup must work after the global gate latches.
        if (this.Timer !== null) { clearInterval(this.Timer); this.Timer = null; }
        if (this.Deadline !== null) { clearTimeout(this.Deadline); this.Deadline = null; }
    };
    private OnDeadline = (): void => { this.OnFailed("The download took too long."); this.Generation++; };
    public Release = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: cleanup must work after navigation or faults.
        this.Stopped = true; this.StopTimer(); this.Button.removeEventListener("click", this.OnClick);
        if (this.Url.length > 0) { URL.revokeObjectURL(this.Url); this.Url = ""; }
    };
}

/** Carries attempt identity across a timeout/retry; stale replies release their blob URL. */
class AppMediaClickAttempt {
    private Owner: AppMediaDownloadClick; private Generation: number;
    public constructor(Owner: AppMediaDownloadClick, Generation: number) { this.Owner = Owner; this.Generation = Generation; }
    public OnUrl = (Url: string): void => { this.Owner.OnUrl(Url, this.Generation); };
    public OnFailed = (Why: string): void => { this.Owner.OnFailed(Why, this.Generation); };
}
